import { Check } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Container, Grid, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PRICING, ROUTES } from '@/config/app.config';
import { useThemeStore } from '@/stores/use-theme-store';

export function PricingSection() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mode } = useThemeStore();

    const plans = [PRICING.plans.abap];

    return (
        <Box
            id="pricing"
            sx={{
                py: { xs: 8, md: 12 },
                bgcolor: mode === 'dark' ? '#1A1C3E' : '#F0F2F5',
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        variant="h3"
                        sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.8rem', md: '2.5rem' } }}
                    >
                        {t('landing.pricing.title')}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                        {t('landing.pricing.subtitle')}
                    </Typography>
                </Box>

                <Grid container spacing={4} justifyContent="center">
                    {plans.map((plan) => (
                        <Grid size={{ xs: 12, md: 5 }} key={plan.id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'visible',
                                    transition: 'all 0.3s ease',
                                    border: '2px solid',
                                    borderColor: 'primary.main',
                                    bgcolor: mode === 'dark' ? '#252851' : '#FFFFFF',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 20px 60px rgba(243,147,35,0.2)',
                                    },
                                }}
                            >

                                <CardContent sx={{ p: 4 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                        {plan.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: 40 }}>
                                        {t(plan.descriptionKey)}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3, gap: 0.5 }}>
                                        <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                            {PRICING.currencySymbol}{plan.price}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            {t('landing.pricing.perMonth')}
                                        </Typography>
                                    </Box>

                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
                                        ~{PRICING.localCurrencySymbol}{plan.localPrice}{t('landing.pricing.perMonth')}
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        onClick={() => navigate(ROUTES.register)}
                                        sx={{
                                            mb: 3,
                                            py: 1.5,
                                            fontWeight: 700,
                                            borderRadius: 3,
                                        }}
                                    >
                                        {t('landing.pricing.subscribe')}
                                    </Button>

                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                        {t('landing.pricing.mcpsIncluded')}:
                                    </Typography>
                                    <List dense disablePadding>
                                        {plan.mcps.map((mcp) => (
                                            <ListItem key={mcp.name} disableGutters sx={{ py: 0.25 }}>
                                                <ListItemIcon sx={{ minWidth: 28 }}>
                                                    <Check sx={{ fontSize: 18, color: 'success.main' }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={mcp.name}
                                                    secondary={t(mcp.descriptionKey)}
                                                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                                                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>

                                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                            {t('landing.pricing.includes')}:
                                        </Typography>
                                        <List dense disablePadding>
                                            {plan.featureKeys.map((featureKey) => (
                                                <ListItem key={featureKey} disableGutters sx={{ py: 0.25 }}>
                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                        <Check sx={{ fontSize: 16, color: 'primary.main' }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={t(featureKey)}
                                                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}

export default PricingSection;
